import { useState, useEffect } from 'react';
import { useOutletContext, useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, Receipt, X, Send, CheckCircle, Euro, Calendar, 
  AlertTriangle, CreditCard
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

const statusOptions = [
  { value: 'draft', label: 'Brouillon', color: 'bg-slate-100 text-slate-600' },
  { value: 'pending_approval', label: 'À valider', color: 'bg-purple-100 text-purple-600' },
  { value: 'sent', label: 'Envoyée', color: 'bg-blue-100 text-blue-600' },
  { value: 'pending', label: 'En attente', color: 'bg-amber-100 text-amber-600' },
  { value: 'paid', label: 'Payée', color: 'bg-green-100 text-green-600' },
  { value: 'overdue', label: 'En retard', color: 'bg-red-100 text-red-600' },
];

const paymentMethods = [
  { value: 'virement', label: 'Virement bancaire' },
  { value: 'cheque', label: 'Chèque' },
  { value: 'especes', label: 'Espèces' },
  { value: 'cb', label: 'Carte bancaire' },
];

export default function FacturesPage() {
  const { user, isAssistant } = useOutletContext();
  const [searchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || '');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('virement');
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);

  useEffect(() => {
    fetchInvoices();
  }, []);

  useEffect(() => {
    // Count pending approval invoices
    const count = invoices.filter(inv => inv.status === 'pending_approval').length;
    setPendingApprovalCount(count);
  }, [invoices]);

  const fetchInvoices = async () => {
    const token = localStorage.getItem('client_token');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/batiment/invoices`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setInvoices(await response.json());
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (invoiceId, newStatus, method = null) => {
    const token = localStorage.getItem('client_token');
    try {
      let url = `${API_URL}/api/batiment/invoices/${invoiceId}/status?status=${newStatus}`;
      if (method) {
        url += `&payment_method=${method}`;
      }

      const response = await fetch(url, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Statut mis à jour');
        fetchInvoices();
        setShowPaymentModal(false);
        if (selectedInvoice?.id === invoiceId) {
          setSelectedInvoice(prev => ({ ...prev, status: newStatus }));
        }
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleMarkAsPaid = () => {
    if (selectedInvoice) {
      handleUpdateStatus(selectedInvoice.id, 'paid', paymentMethod);
    }
  };

  const handleApproveInvoice = async (invoiceId) => {
    // Only clients/admins can approve - this sends the invoice
    if (isAssistant) {
      toast.error("Seul le client peut valider l'envoi");
      return;
    }
    await handleUpdateStatus(invoiceId, 'sent');
    toast.success('Facture approuvée et envoyée !');
  };

  const handleRequestApproval = async (invoiceId) => {
    // Assistants request approval before sending
    await handleUpdateStatus(invoiceId, 'pending_approval');
    toast.success('Demande de validation envoyée au client');
  };

  const openDetailModal = (invoice) => {
    setSelectedInvoice(invoice);
    setShowDetailModal(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getStatusInfo = (status) => statusOptions.find(s => s.value === status) || statusOptions[0];

  const isOverdue = (invoice) => {
    if (invoice.status === 'paid') return false;
    if (!invoice.due_date) return false;
    return new Date(invoice.due_date) < new Date();
  };

  const filteredInvoices = invoices.filter(inv => {
    const matchesSearch = inv.invoice_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         inv.client_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = !filterStatus || inv.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Stats
  const stats = {
    total: filteredInvoices.reduce((sum, i) => sum + (i.total || 0), 0),
    paid: filteredInvoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + (i.total || 0), 0),
    pending: filteredInvoices.filter(i => ['sent', 'pending'].includes(i.status)).reduce((sum, i) => sum + (i.total || 0), 0),
    overdue: filteredInvoices.filter(i => isOverdue(i)).reduce((sum, i) => sum + (i.total || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rm-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="factures-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-rm-text">Factures</h1>
          <p className="text-rm-text-secondary">Suivez tes paiements</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <p className="text-sm text-rm-text-secondary">Total facturé</p>
          <p className="text-2xl font-bold text-rm-text mt-1">{formatCurrency(stats.total)}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <p className="text-sm text-green-600">Payé</p>
          <p className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(stats.paid)}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <p className="text-sm text-amber-600">En attente</p>
          <p className="text-2xl font-bold text-amber-600 mt-1">{formatCurrency(stats.pending)}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <p className="text-sm text-red-600">En retard</p>
          <p className="text-2xl font-bold text-red-600 mt-1">{formatCurrency(stats.overdue)}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher une facture..."
            className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
        >
          <option value="">Tous les statuts</option>
          {statusOptions.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      {/* Invoices List */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100">
        {filteredInvoices.length === 0 ? (
          <div className="text-center py-12">
            <Receipt className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-rm-text-secondary">Aucune facture</p>
            <p className="text-sm text-slate-400 mt-1">Les factures sont créées à partir des devis acceptés</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredInvoices.map((invoice) => {
              const statusInfo = getStatusInfo(invoice.status);
              const overdue = isOverdue(invoice);
              return (
                <div
                  key={invoice.id}
                  onClick={() => openDetailModal(invoice)}
                  className="p-4 hover:bg-slate-50 transition-colors cursor-pointer"
                  data-testid={`invoice-${invoice.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                      invoice.status === 'paid' ? 'bg-green-100' : 
                      overdue ? 'bg-red-100' : 'bg-amber-100'
                    }`}>
                      {overdue ? (
                        <AlertTriangle className="w-6 h-6 text-red-600" />
                      ) : invoice.status === 'paid' ? (
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      ) : (
                        <Receipt className="w-6 h-6 text-amber-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-rm-text">{invoice.invoice_number}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          overdue ? 'bg-red-100 text-red-600' : statusInfo.color
                        }`}>
                          {overdue ? 'En retard' : statusInfo.label}
                        </span>
                      </div>
                      <p className="text-sm text-rm-text-secondary">{invoice.client_name}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-semibold text-rm-text">{formatCurrency(invoice.total)}</p>
                      <p className="text-xs text-rm-text-secondary">
                        Échéance: {formatDate(invoice.due_date)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {showDetailModal && selectedInvoice && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowDetailModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-rm-text">{selectedInvoice.invoice_number}</h2>
                    <p className="text-rm-text-secondary">{selectedInvoice.client_name}</p>
                  </div>
                  <button onClick={() => setShowDetailModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Status */}
                <div>
                  <h4 className="font-semibold text-rm-text mb-2">Statut</h4>
                  <div className="flex flex-wrap gap-2">
                    {statusOptions.filter(s => s.value !== 'paid').map(s => (
                      <button
                        key={s.value}
                        onClick={() => handleUpdateStatus(selectedInvoice.id, s.value)}
                        className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                          selectedInvoice.status === s.value ? s.color : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Info */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-rm-text-secondary">Date de création</span>
                    <p className="font-medium">{formatDate(selectedInvoice.created_at)}</p>
                  </div>
                  <div>
                    <span className="text-rm-text-secondary">Échéance</span>
                    <p className={`font-medium ${isOverdue(selectedInvoice) ? 'text-red-600' : ''}`}>
                      {formatDate(selectedInvoice.due_date)}
                    </p>
                  </div>
                  {selectedInvoice.quote_number && (
                    <div>
                      <span className="text-rm-text-secondary">Devis associé</span>
                      <p className="font-medium">{selectedInvoice.quote_number}</p>
                    </div>
                  )}
                  {selectedInvoice.payment_date && (
                    <div>
                      <span className="text-rm-text-secondary">Date de paiement</span>
                      <p className="font-medium text-green-600">{formatDate(selectedInvoice.payment_date)}</p>
                    </div>
                  )}
                </div>

                {/* Items */}
                {selectedInvoice.items?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-rm-text mb-2">Détail</h4>
                    <div className="bg-slate-50 rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="text-left p-3">Description</th>
                            <th className="text-right p-3">Qté</th>
                            <th className="text-right p-3">P.U.</th>
                            <th className="text-right p-3">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedInvoice.items.map((item, i) => (
                            <tr key={i} className="border-t border-slate-200">
                              <td className="p-3">{item.description}</td>
                              <td className="p-3 text-right">{item.quantity} {item.unit}</td>
                              <td className="p-3 text-right">{formatCurrency(item.unit_price)}</td>
                              <td className="p-3 text-right font-medium">{formatCurrency(item.quantity * item.unit_price)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Totals */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span>Sous-total HT</span>
                    <span>{formatCurrency(selectedInvoice.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span>TVA ({selectedInvoice.tax_rate}%)</span>
                    <span>{formatCurrency(selectedInvoice.tax_amount)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t border-slate-200 pt-2">
                    <span>Total TTC</span>
                    <span className="text-rm-primary">{formatCurrency(selectedInvoice.total)}</span>
                  </div>
                </div>

                {/* Payment Status */}
                {selectedInvoice.status === 'paid' && (
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-green-700 mb-2">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-semibold">Facture payée</span>
                    </div>
                    <p className="text-sm text-green-600">
                      Payée le {formatDate(selectedInvoice.payment_date)} 
                      {selectedInvoice.payment_method && ` par ${selectedInvoice.payment_method}`}
                    </p>
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-slate-100 flex justify-between">
                <div>
                  {selectedInvoice.status !== 'paid' && (
                    <Button onClick={() => setShowPaymentModal(true)} className="bg-green-600 hover:bg-green-700">
                      <CreditCard className="w-4 h-4 mr-2" />
                      Marquer comme payée
                    </Button>
                  )}
                </div>
                <Button onClick={() => setShowDetailModal(false)}>Fermer</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Payment Modal */}
      <AnimatePresence>
        {showPaymentModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowPaymentModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-md shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <h3 className="text-xl font-bold text-rm-text mb-4">Enregistrer le paiement</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-2">Mode de paiement</label>
                    <select
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    >
                      {paymentMethods.map(m => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-rm-text-secondary">Montant</p>
                    <p className="text-2xl font-bold text-rm-primary">{formatCurrency(selectedInvoice?.total)}</p>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <Button variant="outline" onClick={() => setShowPaymentModal(false)}>Annuler</Button>
                  <Button className="bg-green-600 hover:bg-green-700" onClick={handleMarkAsPaid}>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Confirmer le paiement
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
